import { useState, useMemo } from 'react';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { compact } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import { useEnvironment } from '@/react/portainer/environments/queries/useEnvironment';
import { useAuthorizations } from '@/react/hooks/useUser';
import { confirmDestructive } from '@/react/components/modals/confirm';
import { QueueState } from '@/react/common/RequestQueue';

import { useRegistry } from '../../../queries/useRegistry';
import { useRepositoryTags } from '../../queries/useRepositoryTags';
import { getRegistriesLink } from '../../../utils/getRegistriesLink';
import { useRemoveRepository } from '../../queries/useRemoveRepository';
import { useRemoveTags } from '../../queries/useRemoveTags';
import { useTagsDetails } from '../../queries/useTagsDetails';
import type { Tag } from '../TagsDatatable/types';
import { queryKeys } from '../../queries/queryKeys';

export function useRepositoryEditViewData() {
  const queryClient = useQueryClient();

  const {
    params: { id, repository: repositoryName, endpointId },
  } = useCurrentStateAndParams();

  const registryId = Number(id);
  const environmentId = endpointId ? Number(endpointId) : undefined;

  const { data: endpoint } = useEnvironment(environmentId);

  /**
   * Fetches registry configuration and metadata for the current registry.
   * Provides registry details needed for operations and display.
   */
  const { data: registry } = useRegistry(registryId);

  /**
   * Fetches the list of tag names for the current repository.
   * Used for basic repository information display and tag operations.
   */
  const {
    data: tagNamesResponse,
    isLoading: isTagsLoading,
    refetch: refetchTags,
  } = useRepositoryTags({
    registryId,
    repository: repositoryName,
    environmentId,
  });

  /**
   * Tracks repository deletion progress state.
   * Used to show progress UI during repository deletion operations.
   */
  const [repositoryDeletionState, setRepositoryDeletionState] =
    useState<QueueState | null>(null);

  const tagNames = useMemo(() => {
    if (repositoryDeletionState?.status === 'SUCCESS') {
      // If repository deletion is successful, return empty tags to prevent further queries
      return [];
    }
    return tagNamesResponse?.tags ?? [];
  }, [tagNamesResponse, repositoryDeletionState]);

  /**
   * Checks if the current user has permission to delete registry resources.
   * Controls visibility of delete-related UI elements.
   */
  const { authorized: authorizedToDelete } = useAuthorizations(
    'PortainerRegistryInternalDelete'
  );

  /**
   * Checks if the current user has permission to update registry resources.
   * Controls visibility of add/edit tag functionality.
   */
  const { authorized: authorizedToUpdate } = useAuthorizations(
    'PortainerRegistryInternalUpdate'
  );

  /**
   * Provides access to router state service for programmatic navigation.
   * Used for redirecting after repository deletion.
   */
  const router = useRouter();

  /**
   * Mutation hook for removing multiple tags from the repository.
   * Uses dummy manifest strategy for safe deletion.
   */
  const removeTagsMutation = useRemoveTags();

  /**
   * Mutation hook for removing the entire repository.
   * Supports progress tracking for the multi-phase deletion operation.
   */
  const removeRepositoryMutation = useRemoveRepository();

  const breadcrumbs = compact([
    {
      label: 'Registries',
      link: getRegistriesLink(endpoint),
      linkParams: { endpointId: environmentId },
    },
    registry && {
      label: registry.Name,
      link: 'portainer.registries.registry.repositories',
      linkParams: { id: registryId, endpointId: environmentId },
    },
    repositoryName && {
      label: repositoryName,
      link: 'portainer.registries.registry.repository',
    },
  ]);

  /**
   * Handles removing multiple tags from the repository.
   */
  async function handleRemoveTags(selectedTags: Tag[]) {
    if (!selectedTags?.length || !registry) return;

    const selectedTagNames = selectedTags.map((tag) => tag.Name);
    const deletingAll = selectedTagNames.length === tagNames.length;

    removeTagsMutation.mutate(
      {
        registry,
        repository: repositoryName,
        selectedTagNames,
        endpointId: environmentId,
        deletingAll,
      },
      {
        onSuccess: async () => {
          // If all tags were deleted, redirect to repositories list
          if (deletingAll) {
            await router.stateService.go(
              'portainer.registries.registry.repositories',
              {
                id: registry.Id,
                endpointId: environmentId,
              },
              { reload: true }
            );

            // Clear out the repository tags list cache after redirecting, so the current page doesn't try
            // to reload tags for a possibly non-existent repo, but the next page doesn't show stale data
            queryClient.invalidateQueries(
              queryKeys.repositoryTags(registry.Id, repositoryName)
            );
          }
        },
      }
    );
  }

  /**
   * Handles removing the entire repository after user confirmation.
   * Shows a destructive confirmation dialog and performs the deletion operation using queue-based approach.
   * Redirects to the repositories list upon successful completion.
   */
  async function handleRemoveRepository() {
    if (!registry) return;
    const confirmed = await confirmDestructive({
      title: 'Are you sure?',
      message:
        'This action will only remove the manifests linked to this repository. You need to manually trigger a garbage collector pass on your registry to remove orphan layers and really remove the images content. THIS ACTION CAN NOT BE UNDONE',
      confirmButton: {
        label: 'Delete',
        value: true,
        color: 'danger',
        dataCy: 'delete-repository-confirm-btn',
      },
      cancelButtonLabel: 'Cancel',
    });
    if (!confirmed) return;

    // Show modal immediately after confirmation
    setRepositoryDeletionState({
      totalCount: 0,
      completedCount: 0,
      status: 'PROCESSING',
      elapsedTime: 0,
    });

    removeRepositoryMutation.mutate(
      {
        registry,
        repository: repositoryName,
        endpointId: environmentId,
        onStateChange: (state) => {
          setRepositoryDeletionState(state);
        },
      },
      {
        onSuccess: async () => {
          // Navigate away first
          await router.stateService.go(
            'portainer.registries.registry.repositories',
            {
              id: registry.Id,
              endpointId: environmentId,
            },
            { reload: true }
          );

          // Clear out the repository tags list cache after redirecting, so the current page doesn't try
          // to reload tags and the list view doesn't show stale data
          queryClient.invalidateQueries(
            queryKeys.repositoryTags(registry.Id, repositoryName)
          );
        },
      }
    );
  }

  /**
   * Fetches OS/architecture/size/created details for every tag in a single request,
   * rather than one request per tag.
   */
  const { data: tagsDetails } = useTagsDetails({
    registryId,
    repository: repositoryName,
    environmentId,
    tags: tagNames,
  });

  /**
   * Transforms the raw tag names array into the format expected by the TagsDatatable component.
   * Returns empty array during repository deletion to prevent tag detail queries from being created.
   * Memoized to prevent unnecessary recalculations when other state changes.
   */
  const datatableTags: Tag[] = useMemo(
    () => tagNames.map((name) => ({ Name: name, ...tagsDetails?.[name] })),
    [tagNames, tagsDetails]
  );

  return {
    // Data
    breadcrumbs,
    repositoryName,
    tagNames,
    datatableTags,
    actionInProgress:
      repositoryDeletionState?.status === 'PROCESSING' ||
      removeTagsMutation.isLoading,
    canDelete: authorizedToDelete && (datatableTags?.length ?? 0) > 0,
    canUpdate: authorizedToUpdate,
    repositoryDeletionState,
    isTagsLoading,

    // Handlers
    handleRemoveTags,
    handleRemoveRepository,
    refetchTags,
  };
}
